import { useState } from "react";
import { MODELS, useMultiModel } from "../../../api";
import { flags } from "../../../flags";
import { Toolbar } from "../../../design-system/components/Toolbar/Toolbar";
import { highestRole, isStudent, Roles } from "../../../lib/Roles";
import { ROUTES } from "../../../routes.js";
import DemoRestrictedDialog from "../../demo/DemoRestrictedDialog";
import type { AnySparkBag, DemoSparkBag, SparkBag } from "../sparkBag";
import { useCurrentTourStep } from "../user-tour/context/WorkshopUserTourContext.jsx";
import styles from "./toolbarContainer.css";

/**
 * The toolbar inside a Lernreise: the viewer's role in the workshop picks the
 * student or teacher variant, a student's small team drives the upload button,
 * and the guided tour can hold the Spark flyout open.
 */
const WorkshopToolbar = ({
  bag: { workshop, spark, settings },
}: {
  bag: SparkBag;
}) => {
  // If the user is a student in this workshop, get their small team.
  // This checks the global role of the user, not the local role.
  // If a teacher account is a student in a Lernreise, this will lead to issues.
  // But the system does not currently support roles-by-lernreise anyway.
  const [smallTeam] = useMultiModel([
    isStudent(workshop.roles)
      ? MODELS.SMALL_TEAM({ workshopId: workshop.id })
      : null,
  ]);

  const baseUrl = ROUTES.spark.getUrl({
    workshopSlug: workshop.slug,
    sparkSlug: spark.slug,
  });
  const leaveTo = ROUTES.lernreisenOverview.getUrl({ showDashboard: true });

  const userRole = highestRole(workshop.roles);

  // The tour's tools step points at the entries inside the Spark flyout, so the
  // flyout has to be open while that step is on screen.
  const tourStep = useCurrentTourStep();

  return (
    <Toolbar
      mode={userRole === Roles.STUDENT ? "student" : "teacher"}
      features={{
        library: settings?.libraryEnabled,
        glossary: spark.showGlossary,
        isHandbookEnabled: flags.handbook,
      }}
      smallTeam={smallTeam}
      workshopId={workshop.id}
      baseUrl={baseUrl}
      leaveTo={leaveTo}
      sparkMenuOpen={Boolean(tourStep?.data?.openSparkMenu)}
    />
  );
};

/**
 * The toolbar in the demo: always the teacher's, as there is no workshop to
 * hold a role in, and the actions that need one (inviting, options) open a
 * dialog instead of a page. No tour runs here, so the flyout is never forced
 * open.
 */
const DemoToolbar = ({ bag: { spark, settings } }: { bag: DemoSparkBag }) => {
  const [restrictedOpen, setRestrictedOpen] = useState(false);

  return (
    <>
      <Toolbar
        mode="teacher"
        features={{ library: settings?.libraryEnabled }}
        baseUrl={ROUTES.demoLernreise.getUrl({ sparkSlug: spark.slug })}
        leaveTo={ROUTES.demoHome.getUrl()}
        onRestrictedClick={() => setRestrictedOpen(true)}
      />
      <DemoRestrictedDialog open={restrictedOpen} setOpen={setRestrictedOpen} />
    </>
  );
};

/**
 * The spark's toolbar, fixed to the bottom of the screen. Whether a workshop is
 * behind the spark decides which of the two variants above it renders.
 */
export function ToolbarContainer({ bag }: { bag: AnySparkBag }) {
  return (
    <div className={styles.toolbarContainer}>
      {bag.workshop !== null ? (
        <WorkshopToolbar bag={bag} />
      ) : (
        <DemoToolbar bag={bag} />
      )}
    </div>
  );
}
